import { GearSixIcon } from '@phosphor-icons/react'
import { useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetBuildingClientTags } from '@/api/clients/get-building-client-tags'
import { ClientProfileResponse } from '@/api/clients/get-client-profile'
import { useGetMailingList } from '@/api/communication/get-mailing-list'
import { ChipSelectFormField } from '@/components/form/chip-select-form-field'
import { DatePickerFormField } from '@/components/form/date-picker-form-field'
import { PhoneNumberFormField } from '@/components/form/phone-number-form-field'
import { SelectFormField } from '@/components/form/select-form-field'
import { SwitchFormField } from '@/components/form/switch-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { FieldsTable } from '@/components/layout/field-table'
import { Button } from '@/components/ui/button'
import {
  AVATAR_IMAGE_OPTIMIZATION,
  FileUpload,
  IMAGE_UPLOAD_LIMITS,
  useImageUploadErrorToast,
} from '@/components/ui/file-upload'
import { Icon } from '@/components/ui/icon'
import { TextInput } from '@/components/ui/text-input'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'
import { UserRoleEnum } from '@/models/features/user.models'
import { ResidentVehiclesModal } from '@/pages/parking-management/residents-parking/parking-modals'
import { selectBuildingsForFilter } from '@/reducers/buildingSelectorReducer'
import { formatDateOnly } from '@/utils/date'

import {
  canChangeBuilding,
  getPhonePmsTooltip,
  getPmsEditTooltip,
  getPmsTooltip,
  isFieldSynced,
  isFieldVisible,
  PROFILE_PHOTO_ACCEPTED_TYPES,
  PROTECTED_MAILING_LIST_IDS,
  ProfileBeFieldEnum,
} from './general-information.constants'
import * as S from './general-information.styled'
import { GeneralInfoFormValues } from './general-information.types'
import { formatBooleanValue, formatDateOfBirth, type PmsIdInfo } from './general-information.utils'
import { GeneralInformationGroup } from './general-information-group'
import { PmsIndicator } from './general-information-view-fields'

interface EditableTextRowProps {
  label: string
  name: keyof GeneralInfoFormValues
  isSynchronized: boolean
  isLocked?: boolean
  required?: boolean
  pmsTooltip?: string
  maxLength?: number
  mask?: string | string[]
  maskOptions?: {
    clearMaskOnLostFocus?: boolean
    showMaskOnFocus?: boolean
    showMaskOnHover?: boolean
  }
}

const EditableTextRow = ({
  label,
  name,
  isSynchronized,
  isLocked,
  required,
  pmsTooltip,
  maxLength,
  mask,
  maskOptions,
}: EditableTextRowProps) => {
  const { t } = useTranslation(['clients'])

  if (isSynchronized) {
    return (
      <S.FieldRow>
        <S.LabelWrapper>
          <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
            {label}
            {required && <span aria-hidden="true"> *</span>}
          </Typography>
          <PmsIndicator tooltip={pmsTooltip ?? getPmsEditTooltip(t)} />
        </S.LabelWrapper>
        <Tooltip
          fullWidth
          trigger={
            <TextInputFormField
              disabled
              name={name as string}
              size="small"
              maxLength={maxLength}
              mask={mask}
              maskOptions={maskOptions}
            />
          }
        >
          {pmsTooltip ?? getPmsEditTooltip(t)}
        </Tooltip>
      </S.FieldRow>
    )
  }

  if (isLocked) {
    return (
      <S.FieldRow>
        <S.LabelWrapper>
          <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
            {label}
            {required && <span aria-hidden="true"> *</span>}
          </Typography>
        </S.LabelWrapper>
        <TextInputFormField
          disabled
          name={name as string}
          size="small"
          maxLength={maxLength}
          mask={mask}
          maskOptions={maskOptions}
        />
      </S.FieldRow>
    )
  }

  return (
    <S.FieldRow>
      <S.LabelWrapper>
        <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
          {label}
          {required && <span aria-hidden="true"> *</span>}
        </Typography>
      </S.LabelWrapper>
      <TextInputFormField
        clearable
        withRestore
        name={name as string}
        size="small"
        maxLength={maxLength}
        mask={mask}
        maskOptions={maskOptions}
      />
    </S.FieldRow>
  )
}

interface PhotoUploadFieldProps {
  existingImageUrl: string | null
}

const PhotoUploadField = ({ existingImageUrl }: PhotoUploadFieldProps) => {
  const { field: photoField } = useController<GeneralInfoFormValues, 'photo'>({ name: 'photo' })
  const { field: removePhotoField } = useController<GeneralInfoFormValues, 'removeExistingPhoto'>({
    name: 'removeExistingPhoto',
  })

  const handleChange = (files: File[]) => {
    const newFile = files[0] ?? null
    if (newFile) {
      // A new file always supersedes a pending removal of the existing avatar
      removePhotoField.onChange(false)
    } else if (!photoField.value) {
      // No staged file was cleared — the user is removing the existing avatar itself
      removePhotoField.onChange(true)
    }
    photoField.onChange(newFile)
  }
  const handleValidationError = useImageUploadErrorToast({
    acceptedTypes: PROFILE_PHOTO_ACCEPTED_TYPES,
    ...IMAGE_UPLOAD_LIMITS,
  })

  return (
    <FileUpload
      layout="maximized"
      acceptedTypes={PROFILE_PHOTO_ACCEPTED_TYPES}
      {...IMAGE_UPLOAD_LIMITS}
      imageOptimization={AVATAR_IMAGE_OPTIMIZATION}
      files={photoField.value ? [photoField.value] : []}
      existingImageUrl={
        removePhotoField.value
          ? undefined
          : getAssetLink({
              file: existingImageUrl ?? undefined,
              path: 'chitown-avatar/avatar',
            }) || undefined
      }
      onChange={handleChange}
      onValidationError={handleValidationError}
    />
  )
}

interface GeneralInformationEditFieldsProps {
  buildingId?: number
  clientData: ClientProfileResponse
  hiddenFields: string[]
  syncedFields: string[]
  showInHouseDelivery: boolean
  pmsIdInfo: PmsIdInfo | null
  userRole: string | null
}

export const GeneralInformationEditFields = ({
  buildingId,
  clientData,
  hiddenFields,
  syncedFields,
  showInHouseDelivery,
  pmsIdInfo,
  userRole,
}: GeneralInformationEditFieldsProps) => {
  const { t } = useTranslation(['clients', 'common'])
  const { data: buildingTags = [], isLoading: isTagsLoading } = useGetBuildingClientTags({
    buildingId,
  })
  const { data: mailingLists = [], isLoading: isMailingLoading } = useGetMailingList(
    { buildingId: buildingId ?? 0 },
    { enabled: !!buildingId },
  )
  const allBuildings = useSelector(selectBuildingsForFilter)
  const buildingOptions = allBuildings.map((b) => ({ label: b.text, value: b.id }))
  const tagOptions = buildingTags.map((tag) => ({ label: tag, value: tag }))
  const mailingListOptions = mailingLists.map((item) => ({
    label: item.value,
    locked: PROTECTED_MAILING_LIST_IDS.includes(String(item.id)),
    value: String(item.id),
  }))

  // FrontDesk Manager (ShiftManager) can edit the section, but not these identity/location fields.
  const isFrontDeskManager = userRole === UserRoleEnum.enum.ShiftManager

  const isDobSynced = isFieldSynced(ProfileBeFieldEnum.enum.birth, syncedFields)
  const dobDisabledField = (
    <TextInput disabled readOnly size="small" value={formatDateOfBirth(clientData.birth, t)} />
  )

  return (
    <FieldsTable>
      <GeneralInformationGroup
        group="personalAndContact"
        hiddenFields={hiddenFields}
        userRole={userRole}
      >
        {isFieldVisible(null, hiddenFields) && (
          <S.PhotoRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.photo.label')}
              </Typography>
              {isFieldSynced(null, syncedFields) && <PmsIndicator tooltip={getPmsEditTooltip(t)} />}
            </S.LabelWrapper>
            <PhotoUploadField existingImageUrl={clientData.user.avatar} />
          </S.PhotoRow>
        )}

        {isFieldVisible(ProfileBeFieldEnum.enum.firstName, hiddenFields) && (
          <EditableTextRow
            required
            label={t('clients:generalInformation.firstName.label')}
            name="firstName"
            maxLength={255}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.firstName, syncedFields)}
            isLocked={isFrontDeskManager}
          />
        )}

        {isFieldVisible(ProfileBeFieldEnum.enum.lastName, hiddenFields) && (
          <EditableTextRow
            required
            label={t('clients:generalInformation.lastName.label')}
            name="lastName"
            maxLength={255}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.lastName, syncedFields)}
            isLocked={isFrontDeskManager}
          />
        )}

        {/* Email — always locked (V1 exception) */}
        {isFieldVisible(ProfileBeFieldEnum.enum.email, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.email.label')}
              </Typography>
            </S.LabelWrapper>
            <TextInput disabled readOnly size="small" value={clientData.user.email} />
          </S.FieldRow>
        )}

        {isFieldVisible(ProfileBeFieldEnum.enum.phone, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.phoneNumber.label')}
              </Typography>
            </S.LabelWrapper>
            {isFieldSynced(ProfileBeFieldEnum.enum.phone, syncedFields) &&
            userRole !== UserRoleEnum.enum.Client ? (
              <Tooltip fullWidth trigger={<PhoneNumberFormField disabled name="phoneNumber" />}>
                {getPhonePmsTooltip(t)}
              </Tooltip>
            ) : (
              <PhoneNumberFormField disabled={isFrontDeskManager} name="phoneNumber" />
            )}
          </S.FieldRow>
        )}

        {isFieldVisible(ProfileBeFieldEnum.enum.birth, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.dateOfBirth.label')}
              </Typography>
              {isDobSynced && <PmsIndicator tooltip={getPmsEditTooltip(t)} />}
            </S.LabelWrapper>
            {isDobSynced ? (
              <Tooltip fullWidth trigger={dobDisabledField}>
                {getPmsEditTooltip(t)}
              </Tooltip>
            ) : isFrontDeskManager ? (
              dobDisabledField
            ) : (
              <S.DatePickerWrapper>
                <DatePickerFormField
                  withRestore
                  name="dateOfBirth"
                  size="small"
                  minDate={new Date('1900-01-01')}
                  maxDate={new Date()}
                />
              </S.DatePickerWrapper>
            )}
          </S.FieldRow>
        )}
      </GeneralInformationGroup>
      <GeneralInformationGroup group="residence" hiddenFields={hiddenFields} userRole={userRole}>
        {isFieldVisible(ProfileBeFieldEnum.enum.street, hiddenFields) && (
          <EditableTextRow
            required
            label={t('clients:generalInformation.address.label')}
            name="address"
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.street, syncedFields)}
            isLocked={(!pmsIdInfo && userRole === UserRoleEnum.enum.Client) || isFrontDeskManager}
          />
        )}

        {isFieldVisible(ProfileBeFieldEnum.enum.zipCode, hiddenFields) && (
          <EditableTextRow
            required
            label={t('clients:generalInformation.zipCode.label')}
            name="zipCode"
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.zipCode, syncedFields)}
            isLocked={isFrontDeskManager}
          />
        )}

        {/* Building number — always locked (V1 exception) */}
        {isFieldVisible(ProfileBeFieldEnum.enum.buildingNumber, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.buildingNumber.label')}
              </Typography>
            </S.LabelWrapper>
            <TextInput
              disabled
              readOnly
              size="small"
              value={clientData.buildingNumber ?? EMPTY_VALUE}
            />
          </S.FieldRow>
        )}

        {/* Change building — Administrator and Manager only */}
        {canChangeBuilding(userRole) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.buildingId.label')}
              </Typography>
            </S.LabelWrapper>
            <SelectFormField
              searchable
              name="buildingId"
              options={buildingOptions}
              placeholder={t('clients:generalInformation.buildingId.placeholder')}
              size="small"
            />
          </S.FieldRow>
        )}

        {isFieldVisible(ProfileBeFieldEnum.enum.apartment, hiddenFields) && (
          <EditableTextRow
            required
            label={t('clients:generalInformation.unit.label')}
            name="unit"
            maxLength={255}
            isSynchronized={isFieldSynced(ProfileBeFieldEnum.enum.apartment, syncedFields)}
            isLocked={isFrontDeskManager}
          />
        )}

        {/* Floor — always locked (V1 exception) */}
        {isFieldVisible(ProfileBeFieldEnum.enum.floor, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.floor.label')}
              </Typography>
              {isFieldSynced(ProfileBeFieldEnum.enum.floor, syncedFields) && (
                <PmsIndicator tooltip={getPmsEditTooltip(t)} />
              )}
            </S.LabelWrapper>
            <Tooltip
              fullWidth
              trigger={
                <TextInput
                  disabled
                  readOnly
                  size="small"
                  value={clientData.floor ? String(clientData.floor) : EMPTY_VALUE}
                />
              }
              disabled={!isFieldSynced(ProfileBeFieldEnum.enum.floor, syncedFields)}
            >
              {getPmsEditTooltip(t)}
            </Tooltip>
          </S.FieldRow>
        )}
      </GeneralInformationGroup>
      <GeneralInformationGroup group="lease" hiddenFields={hiddenFields} userRole={userRole}>
        {isFieldVisible(ProfileBeFieldEnum.enum.moveInDate, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.moveInDate.label')}
              </Typography>
              {isFieldSynced(ProfileBeFieldEnum.enum.moveInDate, syncedFields) && (
                <PmsIndicator tooltip={getPmsEditTooltip(t)} />
              )}
            </S.LabelWrapper>
            {isFieldSynced(ProfileBeFieldEnum.enum.moveInDate, syncedFields) ? (
              <Tooltip
                fullWidth
                trigger={
                  <TextInput
                    disabled
                    readOnly
                    size="small"
                    value={
                      clientData.moveInDate
                        ? formatDateOnly(new Date(clientData.moveInDate))
                        : EMPTY_VALUE
                    }
                  />
                }
              >
                {getPmsEditTooltip(t)}
              </Tooltip>
            ) : (
              <S.DatePickerWrapper>
                <DatePickerFormField withRestore name="moveInDate" size="small" />
              </S.DatePickerWrapper>
            )}
          </S.FieldRow>
        )}

        {isFieldVisible(ProfileBeFieldEnum.enum.moveOutDate, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.moveOutDate.label')}
              </Typography>
              {isFieldSynced(ProfileBeFieldEnum.enum.moveOutDate, syncedFields) && (
                <PmsIndicator tooltip={getPmsEditTooltip(t)} />
              )}
            </S.LabelWrapper>
            {isFieldSynced(ProfileBeFieldEnum.enum.moveOutDate, syncedFields) ? (
              <Tooltip
                fullWidth
                trigger={
                  <TextInput
                    disabled
                    readOnly
                    size="small"
                    value={
                      clientData.moveOutDate
                        ? formatDateOnly(new Date(clientData.moveOutDate))
                        : EMPTY_VALUE
                    }
                  />
                }
              >
                {getPmsEditTooltip(t)}
              </Tooltip>
            ) : (
              <S.DatePickerWrapper>
                <DatePickerFormField withRestore name="moveOutDate" size="small" />
              </S.DatePickerWrapper>
            )}
          </S.FieldRow>
        )}

        {isFieldVisible(ProfileBeFieldEnum.enum.leaseStart, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.leaseStartDate.label')}
              </Typography>
              {isFieldSynced(ProfileBeFieldEnum.enum.leaseStart, syncedFields) && (
                <PmsIndicator tooltip={getPmsEditTooltip(t)} />
              )}
            </S.LabelWrapper>
            {isFieldSynced(ProfileBeFieldEnum.enum.leaseStart, syncedFields) ? (
              <Tooltip
                fullWidth
                trigger={
                  <TextInput
                    disabled
                    readOnly
                    size="small"
                    value={
                      clientData.leaseStart
                        ? formatDateOnly(new Date(clientData.leaseStart))
                        : EMPTY_VALUE
                    }
                  />
                }
              >
                {getPmsEditTooltip(t)}
              </Tooltip>
            ) : (
              <S.DatePickerWrapper>
                <DatePickerFormField withRestore name="leaseStartDate" size="small" />
              </S.DatePickerWrapper>
            )}
          </S.FieldRow>
        )}

        {isFieldVisible(ProfileBeFieldEnum.enum.leaseEnd, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.leaseEndDate.label')}
              </Typography>
              {isFieldSynced(ProfileBeFieldEnum.enum.leaseEnd, syncedFields) && (
                <PmsIndicator tooltip={getPmsEditTooltip(t)} />
              )}
            </S.LabelWrapper>
            {isFieldSynced(ProfileBeFieldEnum.enum.leaseEnd, syncedFields) ? (
              <Tooltip
                fullWidth
                trigger={
                  <TextInput
                    disabled
                    readOnly
                    size="small"
                    value={
                      clientData.leaseEnd
                        ? formatDateOnly(new Date(clientData.leaseEnd))
                        : EMPTY_VALUE
                    }
                  />
                }
              >
                {getPmsEditTooltip(t)}
              </Tooltip>
            ) : (
              <S.DatePickerWrapper>
                <DatePickerFormField withRestore name="leaseEndDate" size="small" />
              </S.DatePickerWrapper>
            )}
          </S.FieldRow>
        )}
      </GeneralInformationGroup>
      <GeneralInformationGroup group="other" hiddenFields={hiddenFields} userRole={userRole}>
        {/* Pets — always locked (V1 exception) */}
        {isFieldVisible(ProfileBeFieldEnum.enum.pets, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.pets.label')}
              </Typography>
              {isFieldSynced(ProfileBeFieldEnum.enum.pets, syncedFields) && (
                <PmsIndicator tooltip={getPmsEditTooltip(t)} />
              )}
            </S.LabelWrapper>
            <Tooltip
              fullWidth
              trigger={
                <TextInput
                  disabled
                  readOnly
                  size="small"
                  value={
                    clientData.pets != null ? formatBooleanValue(clientData.pets, t) : EMPTY_VALUE
                  }
                />
              }
              disabled={!isFieldSynced(ProfileBeFieldEnum.enum.pets, syncedFields)}
            >
              {getPmsEditTooltip(t)}
            </Tooltip>
          </S.FieldRow>
        )}

        {/* In house delivery — the fallback shown when Door-to-Door isn't effectively available */}
        {isFieldVisible(null, hiddenFields) && showInHouseDelivery && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.inHouseDelivery.label')}
              </Typography>
              {isFieldSynced(null, syncedFields) && <PmsIndicator tooltip={getPmsEditTooltip(t)} />}
            </S.LabelWrapper>
            {isFieldSynced(null, syncedFields) ? (
              <Tooltip
                fullWidth
                trigger={
                  <S.FieldValue>{formatBooleanValue(clientData.inHouse ?? false, t)}</S.FieldValue>
                }
              >
                {getPmsEditTooltip(t)}
              </Tooltip>
            ) : (
              <SwitchFormField name="inHouseDelivery" />
            )}
          </S.FieldRow>
        )}

        {/* PMS ID — visible when client has a PMS ID, or field is synced (shows EMPTY_VALUE) */}
        {isFieldVisible(ProfileBeFieldEnum.enum.pmsExternalId, hiddenFields) &&
          (pmsIdInfo !== null ||
            isFieldSynced(ProfileBeFieldEnum.enum.pmsExternalId, syncedFields)) && (
            <S.FieldRow>
              <S.LabelWrapper>
                <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                  {pmsIdInfo
                    ? t('clients:generalInformation.pmsId.labelWithPmsName', {
                        pmsName: pmsIdInfo.pmsName,
                      })
                    : t('clients:generalInformation.pmsId.label')}
                </Typography>
                <PmsIndicator tooltip={getPmsTooltip(t)} />
              </S.LabelWrapper>
              <TextInput
                disabled
                readOnly
                size="small"
                value={pmsIdInfo?.pmsExternalId ?? EMPTY_VALUE}
              />
            </S.FieldRow>
          )}

        {isFieldVisible(null, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.tags.label')}
              </Typography>
            </S.LabelWrapper>
            <S.FieldValue>
              <ChipSelectFormField
                chipColor="blue"
                loading={isTagsLoading}
                maxItemLength={50}
                name="tags"
                size="small"
                options={tagOptions}
                placeholder={t('clients:generalInformation.tags.placeholder')}
                onCreate={(label) => label}
              />
            </S.FieldValue>
          </S.FieldRow>
        )}

        {isFieldVisible(null, hiddenFields) && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.mailingList.label')}
              </Typography>
            </S.LabelWrapper>
            <S.FieldValue>
              <ChipSelectFormField
                chipColor="purple"
                loading={isMailingLoading}
                name="mailingList"
                size="small"
                options={mailingListOptions}
                placeholder={t('clients:generalInformation.mailingList.placeholder')}
              />
            </S.FieldValue>
          </S.FieldRow>
        )}

        {/* Vehicles */}
        {isFieldVisible(null, hiddenFields) && userRole !== UserRoleEnum.enum.Trainer && (
          <S.FieldRow>
            <S.LabelWrapper>
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:generalInformation.vehicles.label')}
              </Typography>
            </S.LabelWrapper>
            <S.FieldValue>
              <Button
                size="small"
                use="secondary"
                onClick={() =>
                  popupFactory.open(ResidentVehiclesModal, { residentId: Number(clientData.id) })
                }
              >
                <Icon icon={GearSixIcon} size="1.6rem" />
                {t('clients:generalInformation.vehicles.manage')}
              </Button>
            </S.FieldValue>
          </S.FieldRow>
        )}
      </GeneralInformationGroup>
    </FieldsTable>
  )
}
